import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Pencil, Trash2, ExternalLink, Search, Upload, Image as ImageIcon, CheckCircle2, XCircle, Sparkles, RefreshCw } from "lucide-react";

interface AvailableGrant {
  id: string;
  funder: string;
  name: string;
  logo_url: string | null;
  summary: string | null;
  eligibility: string | null;
  amount_min: number | null;
  amount_max: number | null;
  next_deadline: string | null;
  rolling_deadline: boolean;
  website_url: string | null;
  region: string | null;
  is_active: boolean;
  display_order: number;
  is_approved: boolean;
  auto_discovered: boolean;
  discovered_at: string | null;
  source_url: string | null;
}

const EMPTY: Omit<AvailableGrant, "id"> = {
  funder: "",
  name: "",
  logo_url: null,
  summary: "",
  eligibility: "",
  amount_min: null,
  amount_max: null,
  next_deadline: null,
  rolling_deadline: true,
  website_url: "",
  region: "UK",
  is_active: true,
  display_order: 0,
  is_approved: true,
  auto_discovered: false,
  discovered_at: null,
  source_url: null,
};

type FilterTab = "pending" | "approved" | "all";

export default function AvailableGrantsTab() {
  const [rows, setRows] = useState<AvailableGrant[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<FilterTab>("approved");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AvailableGrant | null>(null);
  const [form, setForm] = useState<typeof EMPTY>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [scanning, setScanning] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("available_grants")
      .select("*")
      .order("is_approved", { ascending: true })
      .order("is_active", { ascending: false })
      .order("display_order", { ascending: true })
      .order("funder", { ascending: true });
    if (error) toast.error(error.message);
    setRows((data as AvailableGrant[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const pendingCount = useMemo(() => rows.filter(r => !r.is_approved).length, [rows]);

  const filtered = useMemo(() => {
    let base = rows;
    if (filter === "pending") base = rows.filter(r => !r.is_approved);
    else if (filter === "approved") base = rows.filter(r => r.is_approved);
    if (!search) return base;
    const s = search.toLowerCase();
    return base.filter(r =>
      r.funder.toLowerCase().includes(s) ||
      r.name.toLowerCase().includes(s) ||
      (r.summary ?? "").toLowerCase().includes(s)
    );
  }, [rows, search, filter]);

  const startCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const startEdit = (g: AvailableGrant) => {
    setEditing(g);
    const { id, ...rest } = g;
    setForm({ ...EMPTY, ...rest });
    setOpen(true);
  };

  const handleLogoUpload = async (file: File) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo must be under 2 MB");
      return;
    }
    setUploading(true);
    const ext = file.name.split(".").pop() || "png";
    const path = `${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from("grant-logos").upload(path, file, {
      cacheControl: "3600",
      upsert: false,
      contentType: file.type,
    });
    if (error) {
      toast.error(error.message);
      setUploading(false);
      return;
    }
    const { data } = supabase.storage.from("grant-logos").getPublicUrl(path);
    setForm(f => ({ ...f, logo_url: data.publicUrl }));
    setUploading(false);
    toast.success("Logo uploaded");
  };

  const save = async () => {
    if (!form.funder.trim() || !form.name.trim()) {
      toast.error("Funder and grant name are required");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      summary: form.summary || null,
      eligibility: form.eligibility || null,
      website_url: form.website_url || null,
      region: form.region || "UK",
      next_deadline: form.rolling_deadline ? null : form.next_deadline,
    };
    const { error } = editing
      ? await supabase.from("available_grants").update(payload).eq("id", editing.id)
      : await supabase.from("available_grants").insert(payload);
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success(editing ? "Grant updated" : "Grant added");
    setOpen(false);
    load();
  };

  const remove = async (g: AvailableGrant) => {
    if (!confirm(`Delete "${g.name}" from the directory? This won't affect PTAs that have already added it.`)) return;
    const { error } = await supabase.from("available_grants").delete().eq("id", g.id);
    if (error) toast.error(error.message);
    else { toast.success("Deleted"); load(); }
  };

  const approve = async (g: AvailableGrant) => {
    const { error } = await supabase.from("available_grants").update({ is_approved: true }).eq("id", g.id);
    if (error) toast.error(error.message);
    else { toast.success("Grant approved and now live for PTAs"); load(); }
  };

  const reject = async (g: AvailableGrant) => {
    if (!confirm(`Reject and delete "${g.name}"?`)) return;
    const { error } = await supabase.from("available_grants").delete().eq("id", g.id);
    if (error) toast.error(error.message);
    else { toast.success("Rejected"); load(); }
  };

  const toggleActive = async (g: AvailableGrant) => {
    const { error } = await supabase.from("available_grants").update({ is_active: !g.is_active }).eq("id", g.id);
    if (error) toast.error(error.message);
    else load();
  };

  const runScan = async () => {
    setScanning(true);
    try {
      const { data, error } = await supabase.functions.invoke("scan-grants-weekly", { body: {} });
      if (error) throw error;
      if (data?.skipped) {
        toast.info(data.reason ?? "Scanner skipped");
      } else {
        toast.success(`Scan complete: ${data?.inserted ?? 0} new grants pending approval (${data?.total_found ?? 0} found)`);
        load();
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle>Available Grants Directory</CardTitle>
            <CardDescription>Curated list of UK funders shown to PTAs in the Community dashboard.</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={runScan} disabled={scanning}>
              {scanning ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              {scanning ? "Scanning…" : "Run scan now"}
            </Button>
            <Button onClick={startCreate}><Plus className="h-4 w-4 mr-2" />Add grant</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Tabs value={filter} onValueChange={(v) => setFilter(v as FilterTab)}>
          <TabsList>
            <TabsTrigger value="pending" className="gap-2">
              Pending Approval
              {pendingCount > 0 && (
                <Badge className="bg-yellow-500 text-white hover:bg-yellow-500">{pendingCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="approved">Approved</TabsTrigger>
            <TabsTrigger value="all">All</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search funder, grant or summary…" className="pl-9" />
        </div>

        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading…</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-14">Logo</TableHead>
                <TableHead>Funder / Grant</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Deadline</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">No grants found</TableCell></TableRow>
              ) : filtered.map(g => (
                <TableRow key={g.id} className={g.is_active ? "" : "opacity-50"}>
                  <TableCell>
                    {g.logo_url ? (
                      <img src={g.logo_url} alt={g.funder} className="h-8 w-8 rounded object-contain bg-muted" />
                    ) : (
                      <div className="h-8 w-8 rounded bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
                        {g.funder.slice(0, 2).toUpperCase()}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{g.name}</span>
                      {!g.is_approved && <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">Pending</Badge>}
                      {g.auto_discovered && <Badge variant="outline" className="text-xs">Auto-discovered</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground">{g.funder}</div>
                    {g.source_url && (
                      <a href={g.source_url} target="_blank" rel="noreferrer noopener" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1 mt-0.5">
                        source <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {g.amount_min || g.amount_max
                      ? `£${(g.amount_min ?? 0).toLocaleString()}${g.amount_max ? `–£${g.amount_max.toLocaleString()}` : "+"}`
                      : "—"}
                  </TableCell>
                  <TableCell className="text-sm">
                    {g.rolling_deadline ? <Badge variant="secondary">Rolling</Badge> : g.next_deadline ?? "—"}
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={() => toggleActive(g)}
                      className={`text-xs px-2 py-1 rounded ${g.is_active ? "bg-green-100 text-green-800" : "bg-muted text-muted-foreground"}`}
                    >
                      {g.is_active ? "Active" : "Hidden"}
                    </button>
                  </TableCell>
                  <TableCell className="text-right space-x-1 whitespace-nowrap">
                    {!g.is_approved && (
                      <>
                        <Button size="icon" variant="ghost" onClick={() => approve(g)} title="Approve">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => reject(g)} title="Reject">
                          <XCircle className="h-4 w-4 text-destructive" />
                        </Button>
                      </>
                    )}
                    {g.website_url && (
                      <Button size="icon" variant="ghost" asChild>
                        <a href={g.website_url} target="_blank" rel="noreferrer noopener"><ExternalLink className="h-4 w-4" /></a>
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => startEdit(g)}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(g)}><Trash2 className="h-4 w-4 text-foreground" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit grant" : "Add grant to directory"}</DialogTitle>
            <DialogDescription>This appears in the Community dashboard's "Available Grants" tab for all PTAs.</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Funder *</Label>
              <Input value={form.funder} onChange={(e) => setForm({ ...form, funder: e.target.value })} placeholder="e.g. Tesco" />
            </div>
            <div className="space-y-1.5">
              <Label>Grant name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Stronger Starters" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Logo</Label>
            <div className="flex items-center gap-3">
              {form.logo_url ? (
                <img src={form.logo_url} alt="logo" className="h-12 w-12 rounded object-contain bg-muted" />
              ) : (
                <div className="h-12 w-12 rounded bg-muted flex items-center justify-center">
                  <ImageIcon className="h-5 w-5 text-muted-foreground" />
                </div>
              )}
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && handleLogoUpload(e.target.files[0])}
                />
                <Button type="button" variant="outline" size="sm" asChild disabled={uploading}>
                  <span><Upload className="h-4 w-4 mr-2" />{uploading ? "Uploading…" : (form.logo_url ? "Replace" : "Upload")}</span>
                </Button>
              </label>
              {form.logo_url && (
                <Button type="button" variant="ghost" size="sm" onClick={() => setForm({ ...form, logo_url: null })}>Remove</Button>
              )}
              <Input
                value={form.logo_url ?? ""}
                onChange={(e) => setForm({ ...form, logo_url: e.target.value || null })}
                placeholder="…or paste image URL"
                className="flex-1"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Summary</Label>
            <Textarea rows={3} value={form.summary ?? ""} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="One or two lines describing what this grant funds." />
          </div>

          <div className="space-y-1.5">
            <Label>Eligibility</Label>
            <Textarea rows={2} value={form.eligibility ?? ""} onChange={(e) => setForm({ ...form, eligibility: e.target.value })} placeholder="Who can apply, any conditions." />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Amount min (£)</Label>
              <Input type="number" min={0} value={form.amount_min ?? ""} onChange={(e) => setForm({ ...form, amount_min: e.target.value === "" ? null : Number(e.target.value) })} />
            </div>
            <div className="space-y-1.5">
              <Label>Amount max (£)</Label>
              <Input type="number" min={0} value={form.amount_max ?? ""} onChange={(e) => setForm({ ...form, amount_max: e.target.value === "" ? null : Number(e.target.value) })} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Website</Label>
              <Input value={form.website_url ?? ""} onChange={(e) => setForm({ ...form, website_url: e.target.value })} placeholder="https://…" />
            </div>
            <div className="space-y-1.5">
              <Label>Region</Label>
              <Input value={form.region ?? "UK"} onChange={(e) => setForm({ ...form, region: e.target.value })} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Next deadline</Label>
              <Input type="date" disabled={form.rolling_deadline} value={form.next_deadline ?? ""} onChange={(e) => setForm({ ...form, next_deadline: e.target.value || null })} />
            </div>
            <div className="space-y-1.5">
              <Label>Display order</Label>
              <Input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: Number(e.target.value) || 0 })} />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label>Rolling deadline</Label>
              <p className="text-xs text-muted-foreground">No fixed cut-off — applications accepted any time.</p>
            </div>
            <Switch checked={form.rolling_deadline} onCheckedChange={(v) => setForm({ ...form, rolling_deadline: v })} />
          </div>

          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label>Active</Label>
              <p className="text-xs text-muted-foreground">Hidden grants aren't shown to PTAs.</p>
            </div>
            <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
          </div>

          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label>Approved</Label>
              <p className="text-xs text-muted-foreground">Only approved grants appear in PTAs' Available Grants list.</p>
            </div>
            <Switch checked={form.is_approved} onCheckedChange={(v) => setForm({ ...form, is_approved: v })} />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving}>{saving ? "Saving…" : (editing ? "Save changes" : "Add grant")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
